import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock, Loader2, MapPin, Search, Ticket, Users } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Ok, Panel, Tag } from "@/components/community/kit";
import { ROLE_TONE, communityBase, day } from "@/lib/community";

const TYPES = [["community", "Driver Community"], ["association", "Driver Association"],
  ["union", "Union"], ["professional", "Professional Organization"], ["other", "Other"]];

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = communityBase();
  const [mine, setMine] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [next, setNext] = useState({});
  const [query, setQuery] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showApply, setShowApply] = useState(false);
  const [apply, setApply] = useState({ name: "", type: "community", city: "", state: "", note: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: d }] = await Promise.all([
        api.get("/communities/mine"),
        api.get("/communities/directory", { params: { q: query } }),
      ]);
      setMine(m);
      setDirectory(d.communities);
      api.get("/communities/calendar/next")
        .then(({ data }) => setNext(data.next)).catch(() => {});
    } catch (e) { setError(errMsg(e)); }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy("join"); setError(""); setNotice("");
    try {
      const { data } = await api.post("/communities/join", { code: code.trim().toUpperCase() });
      if (data.status === "active") navigate(`${base}/${data.community.id}`);
      else { setNotice("Request sent — a leader will let you in."); setCode(""); await load(); }
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const request = async (id) => {
    setBusy(id); setError(""); setNotice("");
    try {
      const { data } = await api.post(`/communities/${id}/request`, { note: "" });
      if (data.status === "active") navigate(`${base}/${id}`);
      else { setNotice("Request sent — a leader will let you in."); await load(); }
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const submitApply = async () => {
    setBusy("apply"); setError(""); setNotice("");
    try {
      await api.post("/communities/apply", apply);
      setNotice("Your application is with the LimoLinkX owner. You'll hear back by email.");
      setShowApply(false);
      setApply({ name: "", type: "community", city: "", state: "", note: "" });
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!mine) {
    return (
      <div data-testid="community-loading" className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="community-hub" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Driver community
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Your city's drivers, in one place — independent of whoever you drive for. Chat, meetings,
          elections and dues, run by the members. LimoLinkX takes nothing from it.
        </p>
      </div>

      <Err testId="community-error">{error}</Err>
      <Ok testId="community-notice">{notice}</Ok>

      <Panel testId="community-mine" title="Your communities"
        sub={mine.platform_admin ? "You oversee every community on the platform." : null}>
        {mine.memberships.length === 0 ? (
          <Empty testId="community-mine-empty">
            You are not in a community yet. Enter a code below, or find your city in the directory.
          </Empty>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {mine.memberships.map((c) => (
              <button key={c.id} data-testid={`community-card-${c.id}`}
                onClick={() => navigate(`${base}/${c.id}`)}
                className="group min-w-0 rounded-xl border border-ink-600 bg-ink-900/60 p-4 text-left transition-colors hover:border-gold/40">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-display text-base text-slate-100">{c.name}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {[c.city, c.state].filter(Boolean).join(", ") || c.type_label}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-600 transition-colors group-hover:text-gold" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Chip className={ROLE_TONE[c.my_role] || ROLE_TONE.member}>
                    {c.my_status === "pending" ? "Waiting for approval" : c.my_role_label || "Member"}
                  </Chip>
                  <Chip><Users className="mr-1 h-3 w-3" />{c.counts?.members || 0}</Chip>
                  {c.code && <Chip testId={`community-code-${c.id}`}>{c.code}</Chip>}
                </div>
                {next[c.id] && (
                  <p data-testid={`community-next-${c.id}`}
                    className="mt-3 flex min-w-0 items-center gap-1.5 border-t border-ink-600 pt-2.5 text-xs text-gold">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 truncate">
                      Next: {next[c.id].title} · {day(next[c.id].when)}
                    </span>
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </Panel>

      {!mine.platform_admin && (
        <Panel testId="community-join" title="Join with a code"
          sub="A leader gives you a code like HOU-7X29.">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input data-testid="community-code-input" value={code} placeholder="HOU-7X29"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="font-mono uppercase tracking-[0.2em]" />
            <Button data-testid="community-join-btn" onClick={join} disabled={code.length < 4 || busy === "join"}>
              {busy === "join" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ticket className="mr-2 h-4 w-4" />}
              Join
            </Button>
          </div>
        </Panel>
      )}

      <Panel testId="community-directory" title="Find your city"
        action={
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input data-testid="community-search" value={query} placeholder="City or name"
              onChange={(e) => setQuery(e.target.value)} className="w-full pl-9 sm:w-56" />
          </div>
        }>
        {directory.length === 0 ? (
          <Empty testId="community-directory-empty">
            No community listed for that search yet.
          </Empty>
        ) : (
          <ul className="divide-y divide-ink-600">
            {directory.map((c) => (
              <li key={c.id} data-testid={`directory-row-${c.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-100">{c.name}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                    <MapPin className="h-3 w-3" />
                    {[c.city, c.state].filter(Boolean).join(", ") || "—"} · {c.type_label} · {c.members} members
                  </p>
                </div>
                {c.joined ? (
                  <Chip testId={`directory-joined-${c.id}`} className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
                    Joined
                  </Chip>
                ) : mine.platform_admin ? (
                  <Chip>{c.membership_label}</Chip>
                ) : (
                  <Button data-testid={`directory-request-${c.id}`} size="sm" variant="outline"
                    onClick={() => request(c.id)} disabled={busy === c.id}>
                    {busy === c.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {c.membership === "open" ? "Join" : "Ask to join"}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {mine.can_apply && (
        <Panel testId="community-apply" title="Nothing for your city?"
          sub="Ask LimoLinkX to open one. You become its first owner."
          action={
            <Button data-testid="community-apply-open" size="sm" variant="outline"
              onClick={() => setShowApply((v) => !v)}>
              {showApply ? "Cancel" : "Apply to start one"}
            </Button>
          }>
          {mine.applications.length > 0 && (
            <ul className="mb-4 space-y-2">
              {mine.applications.map((a) => (
                <li key={a.id} data-testid={`application-row-${a.id}`}
                  className="rounded-xl border border-ink-600 bg-ink-900/60 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-200">{a.name}</p>
                    <Chip className={a.status === "approved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : a.status === "declined" ? "border-red-500/40 bg-red-500/10 text-red-300" : ""}>
                      {a.status}
                    </Chip>
                  </div>
                  {a.note && <p className="mt-1.5 text-xs text-slate-500">{a.note}</p>}
                </li>
              ))}
            </ul>
          )}
          {showApply && (
            <div className="space-y-3">
              <Input data-testid="apply-name" placeholder="Houston Black Car Drivers"
                value={apply.name} onChange={(e) => setApply({ ...apply, name: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-3">
                <select data-testid="apply-type" value={apply.type}
                  onChange={(e) => setApply({ ...apply, type: e.target.value })}
                  className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                  {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <Input data-testid="apply-city" placeholder="City" value={apply.city}
                  onChange={(e) => setApply({ ...apply, city: e.target.value })} />
                <Input data-testid="apply-state" placeholder="State" value={apply.state}
                  onChange={(e) => setApply({ ...apply, state: e.target.value })} />
              </div>
              <Textarea data-testid="apply-note" rows={3} value={apply.note}
                placeholder="Who is behind this and roughly how many drivers you expect."
                onChange={(e) => setApply({ ...apply, note: e.target.value })} />
              <Button data-testid="apply-submit" onClick={submitApply}
                disabled={apply.name.trim().length < 3 || busy === "apply"}>
                {busy === "apply" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Send application
              </Button>
            </div>
          )}
        </Panel>
      )}

      <Tag>Signed in as {user?.name} · {user?.role}</Tag>
    </div>
  );
}
